import { watch } from 'vue'
import { RoomEvent, Track as LKTrack } from 'livekit-client'

import type { RemoteParticipant, RemoteTrackPublication, Room } from 'livekit-client'
import type {
  ConnectionSideEvidence,
  ConnectionUplinkSample,
  ReceptionAudioStats,
  ReceptionVerdict,
  ReceptionVideoStats,
} from '@ts-classroom/interfaces'

import { refRoom } from '@hooks/classroom/room-live-kit.hook'

import { RECEPTION_SHARE_HEARTBEAT_MS, RECEPTION_SHARE_TTL_MS } from '@config/connection-quality'
import { createReceptionTracker } from '@utils/classroom/connection-tier'

interface ConnectionReceptionLiveKitReturn {
  sample: (uplink: ConnectionUplinkSample) => Promise<ReceptionVerdict>
  ownEvidence: () => ConnectionSideEvidence
  peerEvidence: () => ConnectionSideEvidence | null
  reset: () => void
}

interface SharedEvidence {
  evidence: ConnectionSideEvidence
  at: number
}

interface InboundRtpStats {
  type: string
  framesPerSecond?: number
  totalFreezesDuration?: number
  bytesReceived?: number
  concealedSamples?: number
  silentConcealedSamples?: number
  totalSamplesReceived?: number
}

const RECEPTION_TOPIC = 'connection-reception'

export const cleanEvidence = (): ConnectionSideEvidence => ({
  isDegraded: false,
  isCpuLimited: false,
  isUplinkImpaired: false,
  frozenSeconds: null,
  concealmentRatio: null,
})

const cleanReception = (): ReceptionVerdict => ({
  isDegraded: false,
  isStalled: false,
  frozenSeconds: null,
  concealmentRatio: null,
})

const tracker = createReceptionTracker()

let latestOwn: ConnectionSideEvidence = cleanEvidence()
let sharedWithPeer: SharedEvidence | null = null
let sharedByPeer: SharedEvidence | null = null

const asNumberOrNull = (value: unknown): number | null => (typeof value === 'number' ? value : null)

const decodePayload = (bytes: Uint8Array): ConnectionSideEvidence | null => {
  try {
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as Partial<ConnectionSideEvidence>
    if (typeof parsed.isDegraded !== 'boolean') return null

    return {
      isDegraded: parsed.isDegraded,
      isCpuLimited: parsed.isCpuLimited === true,
      isUplinkImpaired: parsed.isUplinkImpaired === true,
      frozenSeconds: asNumberOrNull(parsed.frozenSeconds),
      concealmentRatio: asNumberOrNull(parsed.concealmentRatio),
    }
  } catch {
    return null
  }
}

const handleData = (payload: Uint8Array, _participant?: RemoteParticipant, _kind?: unknown, topic?: string): void => {
  if (topic !== RECEPTION_TOPIC) return

  const evidence = decodePayload(payload)
  if (!evidence) return

  sharedByPeer = { evidence, at: Date.now() }
}

watch(refRoom, (room) => {
  room?.on(RoomEvent.DataReceived, handleData)
}, { immediate: true })

const firstRemote = (room: Room): RemoteParticipant | null => [...room.remoteParticipants.values()][0] ?? null

const readInboundRtp = async (publication: RemoteTrackPublication): Promise<InboundRtpStats | null> => {
  const receiver = publication.track?.receiver
  if (!receiver) return null

  const report = await receiver.getStats()
  const inbound: InboundRtpStats | undefined = [...report.values()].find((stats) => stats.type === 'inbound-rtp')

  return inbound ?? null
}

const isReceiving = (publication: RemoteTrackPublication | undefined): publication is RemoteTrackPublication =>
  !!publication?.track && publication.isSubscribed && !publication.isMuted

const readVideo = async (publication: RemoteTrackPublication | undefined): Promise<ReceptionVideoStats | null> => {
  if (!isReceiving(publication) || !publication.isEnabled) return null

  const inbound = await readInboundRtp(publication)
  if (!inbound) return null

  return {
    framesPerSecond: inbound.framesPerSecond ?? null,
    totalFreezesDuration: inbound.totalFreezesDuration ?? null,
    bytesReceived: inbound.bytesReceived ?? 0,
    isPaused: publication.track?.streamState === LKTrack.StreamState.Paused,
  }
}

const readAudio = async (publication: RemoteTrackPublication | undefined): Promise<ReceptionAudioStats | null> => {
  if (!isReceiving(publication)) return null

  const inbound = await readInboundRtp(publication)
  if (!inbound) return null

  return {
    concealedSamples: inbound.concealedSamples ?? 0,
    silentConcealedSamples: inbound.silentConcealedSamples ?? 0,
    totalSamplesReceived: inbound.totalSamplesReceived ?? 0,
    bytesReceived: inbound.bytesReceived ?? 0,
  }
}

const isWorthRepeating = (evidence: ConnectionSideEvidence): boolean =>
  evidence.isDegraded || evidence.isCpuLimited || evidence.isUplinkImpaired

const isShareDue = (evidence: ConnectionSideEvidence, now: number): boolean => {
  if (!sharedWithPeer) return isWorthRepeating(evidence)

  const shared = sharedWithPeer.evidence
  const hasChanged = shared.isDegraded !== evidence.isDegraded
    || shared.isCpuLimited !== evidence.isCpuLimited
    || shared.isUplinkImpaired !== evidence.isUplinkImpaired
  if (hasChanged) return true

  return isWorthRepeating(evidence) && now - sharedWithPeer.at >= RECEPTION_SHARE_HEARTBEAT_MS
}

const shareWithPeer = async (room: Room, evidence: ConnectionSideEvidence, now: number): Promise<void> => {
  if (!isShareDue(evidence, now)) return

  sharedWithPeer = { evidence, at: now }

  await room.localParticipant.publishData(
    new TextEncoder().encode(JSON.stringify(evidence)) as Uint8Array<ArrayBuffer>,
    { reliable: true, topic: RECEPTION_TOPIC },
  )
}

export const useConnectionReceptionLiveKit = (): ConnectionReceptionLiveKitReturn => {
  const sample = async (uplink: ConnectionUplinkSample): Promise<ReceptionVerdict> => {
    const room = refRoom.value
    const peer = room ? firstRemote(room) : null
    if (!room || !peer) return cleanReception()

    const [video, audio] = await Promise.all([
      readVideo(peer.getTrackPublication(LKTrack.Source.Camera)),
      readAudio(peer.getTrackPublication(LKTrack.Source.Microphone)),
    ])
    const now = Date.now()
    const verdict = tracker.push({ at: now, video, audio })
    latestOwn = {
      isDegraded: verdict.isDegraded,
      isCpuLimited: uplink.isCpuLimited,
      isUplinkImpaired: uplink.isImpaired,
      frozenSeconds: verdict.frozenSeconds,
      concealmentRatio: verdict.concealmentRatio,
    }

    await shareWithPeer(room, latestOwn, now).catch((error) => {
      console.error('[classroom] sharing connection reception failed', error)
    })

    return verdict
  }

  const ownEvidence = (): ConnectionSideEvidence => latestOwn

  const peerEvidence = (): ConnectionSideEvidence | null =>
    (sharedByPeer && Date.now() - sharedByPeer.at < RECEPTION_SHARE_TTL_MS ? sharedByPeer.evidence : null)

  const reset = (): void => {
    tracker.reset()
    latestOwn = cleanEvidence()
    sharedWithPeer = null
    sharedByPeer = null
  }

  return { sample, ownEvidence, peerEvidence, reset }
}
